import React, { useEffect, useRef, useState } from "react";
import { Globe, Check, X } from "lucide-react";
import { LANGS, setLanguage, currentLang, getSuggestion, dismissSuggestion, nameOf, T } from "../../lib/fc3i18n";

export function LanguageMenu({ compact = false }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const cur = currentLang();
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}
        aria-label={T("Change language")} data-testid="lang-menu"
        className="inline-flex items-center gap-1.5 min-h-[44px] rounded-full px-3 text-[14px] font-bold text-[#2B2140] hover:bg-[#F6F3FF] transition-colors">
        <Globe className="w-[18px] h-[18px]" aria-hidden="true" />
        {!compact && <span>{nameOf(cur)}</span>}
      </button>
      {open && (
        <ul role="listbox" aria-label={T("Language")}
          className="absolute end-0 mt-2 w-[260px] max-h-[60vh] overflow-y-auto rounded-2xl bg-white border border-[#F1E6EA] shadow-[0_30px_60px_-30px_rgba(43,33,64,0.45)] p-2 z-[60] grid grid-cols-1">
          {LANGS.map(([code, name]) => (
            <li key={code} role="option" aria-selected={code === cur}>
              <button type="button" lang={code}
                onClick={() => { setOpen(false); setLanguage(code); }}
                className={`w-full flex items-center justify-between rounded-xl px-3 py-2.5 text-[15px] font-semibold text-start ${code === cur ? "bg-[#F6F3FF] text-[#5A49E0]" : "text-[#1E1A2B] hover:bg-[#FFF8F9]"}`}>
                {name}
                {code === cur && <Check className="w-4 h-4" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function LangSuggestBar() {
  const s = getSuggestion();
  const cur = currentLang();
  if (!s || s === cur) return null;
  return (
    <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:end-6 z-[70] sm:max-w-[420px] rounded-2xl bg-white border border-[#EADFF7] shadow-[0_30px_60px_-30px_rgba(43,33,64,0.5)] p-4 flex items-center gap-3" role="region" aria-label="Language suggestion">
      <Globe className="w-6 h-6 text-[#6B5BEF] shrink-0" aria-hidden="true" />
      <p className="flex-1 text-[15px] font-semibold text-[#1E1A2B]">
        {T("Prefer {{lang}}?", { lang: nameOf(s) })}
      </p>
      <button type="button" onClick={() => { dismissSuggestion(); setLanguage(s); }}
        className="min-h-[44px] rounded-full px-4 font-extrabold text-white bg-[#6B5BEF]" lang={s}>
        {nameOf(s)}
      </button>
      <button type="button" onClick={dismissSuggestion} aria-label="Keep English"
        className="min-h-[44px] min-w-[44px] rounded-full text-[#5F5A70] hover:bg-[#F6F3FF] flex items-center justify-center">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}
